import type { RouteDataMeta } from './route'
import type { FlagVerification } from './flagWalk'

// Préparation des routes de déneigement (docs/routes/README.md) : routes par saison, adresses de service
// éligibles des contrats signés, ordre décidé par l'utilisateur. Le serveur fait autorité sur l'ordre,
// les versions et les totaux; ces fonctions ne font que préparer les commandes et lire la projection.

export interface PlanningRoute {
  id: string
  name: string
  color: string
  /** Consignes courtes de la route (500 caractères), lues par l'application des opérateurs ; nulle si aucune. */
  description: string | null
  /** Nulle pour une route héritée (avant les routes par saison) qui porte encore une adresse de la saison. */
  season: string | null
  displayOrder: number | null
  version: number
  memberCount: number
  /** Dernière modification de la route (nom, couleur, description, adresses, ordre) ; nulle si inconnue. */
  updatedAt: string | null
  /** Prénom et nom de l'employé qui l'a faite ; nul si l'auteur n'a pas de fiche employé. */
  updatedByName: string | null
}

export interface PlannedLocation {
  id: string
  contractId: string
  clientPropertyId: string | null
  clientName: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  postalCode: string | null
  /** Position effective : ajustée sur la carte si elle existe, sinon géocodage (règle serveur `effective_latitude`). */
  latitude: number | null
  longitude: number | null
  /** La position a été ajustée à la main (entrée, stationnement) : elle prime sur le géocodage. */
  adjusted: boolean
  /** Version de la position, attendue par `set_service_location_position_v1`. */
  positionVersion: number
  /** Drapeau (point d'approche qui fera passer la résidence « en cours »), distinct de la position ; nul si aucun. */
  flagLatitude?: number | null | undefined
  flagLongitude?: number | null | undefined
  flagSource?: string | null | undefined
  /** Version du drapeau ; la vérification manuelle ne vaut que pour cette version. */
  flagVersion?: number | undefined
  /** Vérification manuelle valide du drapeau actuel (badge ✓ de l'éditeur), sinon nulle. */
  flagVerification?: FlagVerification | null | undefined
  /** Rayon de capture propre à la résidence (m) ; nul = rayon par défaut. */
  flagCaptureRadiusM?: number | null | undefined
  routeId: string | null
  position: number | null
}

export interface RoutePlanningTotals {
  eligible: number
  unassigned: number
  unlocated: number
  unassignedUnlocated: number
}

export interface IneligibleRemoval {
  serviceLocationId: string | null
  routeId: string
  addressLine1: string | null
  addressLine2: string | null
  occurredAt: string
}

export interface RoutePlanningSnapshot {
  season: string
  seasons: string[]
  routes: PlanningRoute[]
  /** Ordre stable de la liste source : rue, numéro civique (tri naturel), unité. */
  locations: PlannedLocation[]
  totals: RoutePlanningTotals
  recentIneligibleRemovals: IneligibleRemoval[]
  /** Rayon de capture par défaut (profil actif, sinon 7 m) ; nul avant la projection v2. */
  defaultCaptureRadiusM?: number | null | undefined
  meta: RouteDataMeta
}

export interface PlaceRouteLocationsCommand {
  idempotencyKey: string
  routeId: string
  locationIds: string[]
  /** Version courante de chaque route touchée : la cible et les routes d'origine des adresses. */
  expectedVersions: Record<string, number>
  beforeLocationId?: string
  afterLocationId?: string
}

export type PlacementRefusalReason = 'NOT_FOUND' | 'OTHER_SEASON' | 'INELIGIBLE'

export interface PlacementRefusal {
  locationId: string
  reason: PlacementRefusalReason
}

export type PlaceRouteLocationsResult =
  | {
      status: 'APPLIED'
      routeId: string
      placed: number
      movedFromOtherRoutes: number
      alreadyInRoute: number
      reordered: boolean
      routeVersions: Record<string, number>
    }
  | { status: 'REJECTED'; refused: PlacementRefusal[] }

export interface RemoveRouteLocationCommand {
  idempotencyKey: string
  routeId: string
  locationId: string
  expectedVersion: number
}

export interface RemovalUndo {
  routeId: string
  locationId: string
  afterLocationId: string | null
  beforeLocationId: string | null
}

export interface RemoveRouteLocationResult {
  routeId: string
  routeVersion: number
  undo: RemovalUndo
}

export interface RoutePlacementPreviewInput {
  routeId: string
  locationIds: string[]
}

export interface RoutePlacementPreview {
  routeId: string
  unassigned: number
  alreadyInRoute: number
  movedFrom: { routeId: string; count: number }[]
  refused: PlacementRefusal[]
}

export interface CreateRouteCommand {
  idempotencyKey: string
  season: string
  name: string
  color: string
}

export interface UpdateRouteCommand {
  idempotencyKey: string
  routeId: string
  name?: string
  color?: string
  /** Vide ou null = effacer la description. */
  description?: string | null
}

/** Longueur maximale d'une description de route (contrainte serveur). */
export const maxRouteDescriptionLength = 500

/**
 * Ajuster la position d'intervention d'une adresse de service (mode ajustement). `latitude`/`longitude` nulles =
 * retour aux coordonnées de géocodage. Ne touche ni l'adresse, ni le contrat, ni la route, ni l'ordre.
 */
export interface SetLocationPositionCommand {
  idempotencyKey: string
  locationId: string
  expectedVersion: number
  latitude: number | null
  longitude: number | null
}

export interface LocationPositionResult {
  locationId: string
  latitude: number
  longitude: number
  adjusted: boolean
  version: number
  /** Position ajustée avant la commande (nulle = géocodage) : ce que rétablit « Annuler ». */
  previous: { adjustedLatitude: number | null; adjustedLongitude: number | null }
}

// ------------------------------------------------------------------ Saison

const seasonPattern = /^\d{4}-\d{4}$/

export function isSeasonLabel(value: string): boolean {
  return seasonPattern.test(value)
}

/**
 * Saison proposée à l'ouverture de l'écran, avant que la projection ne liste les saisons réelles.
 * Choix d'interface seulement (l'utilisateur peut changer) : à partir d'août, on prépare l'hiver qui vient.
 */
export function defaultPlanningSeason(today: Date): string {
  const year = today.getFullYear()
  const start = today.getMonth() >= 7 ? year : year - 1
  return `${String(start)}-${String(start + 1)}`
}

// ---------------------------------------------------------------- Couleurs

/**
 * Palette des routes (maquette Claude Design « RECA Routes », 11 oct. 2026) : ni gris (réservé aux non-affectées)
 * ni rouge (action principale et retrait). Chaque couleur a un nom, lu par les lecteurs d'écran.
 */
export const routeColorPalette = [
  { color: '#3B82F6', name: 'Bleu' },
  { color: '#F5B82E', name: 'Ambre' },
  { color: '#22C55E', name: 'Vert' },
  { color: '#A855F7', name: 'Violet' },
  { color: '#22D3EE', name: 'Cyan' },
  { color: '#F97316', name: 'Orange' },
  { color: '#EC4899', name: 'Rose' },
  { color: '#A3E635', name: 'Lime' },
  { color: '#6366F1', name: 'Indigo' },
  { color: '#14B8A6', name: 'Sarcelle' },
  { color: '#D6A77A', name: 'Sable' },
] as const

/** Couleur des adresses non affectées. */
export const unassignedColor = '#8A93A6'

export function routeColorName(color: string): string {
  return routeColorPalette.find((entry) => entry.color === color.toUpperCase())?.name ?? color
}

const hexColorPattern = /^#[0-9A-Fa-f]{6}$/

/** Même règle que le serveur (`route_color_is_neutral`) : écart entre composantes inférieur à 40. */
export function isReservedNeutralColor(color: string): boolean {
  if (!hexColorPattern.test(color)) return false
  const channels = [1, 3, 5].map((offset) => Number.parseInt(color.slice(offset, offset + 2), 16))
  return Math.max(...channels) - Math.min(...channels) < 40
}

export function isAcceptableRouteColor(color: string): boolean {
  return hexColorPattern.test(color) && !isReservedNeutralColor(color)
}

/** Première couleur de la palette qu'aucune route n'utilise encore, sinon la première. */
export function nextRouteColor(routes: readonly PlanningRoute[]): string {
  const used = new Set(routes.map((route) => route.color.toUpperCase()))
  return (routeColorPalette.find((entry) => !used.has(entry.color)) ?? routeColorPalette[0]).color
}

// ------------------------------------------------------- Lecture de la projection

export function routeMembers(snapshot: RoutePlanningSnapshot, routeId: string): PlannedLocation[] {
  return snapshot.locations
    .filter((location) => location.routeId === routeId)
    .sort((left, right) => (left.position ?? 0) - (right.position ?? 0))
}

export function unassignedLocations(snapshot: RoutePlanningSnapshot): PlannedLocation[] {
  return snapshot.locations.filter((location) => location.routeId === null)
}

export function isLocated(location: PlannedLocation): boolean {
  return location.latitude !== null && location.longitude !== null
}

/** Routes de la saison à proposer comme destinations (les routes héritées ne reçoivent plus d'adresses). */
export function seasonRoutes(snapshot: RoutePlanningSnapshot): PlanningRoute[] {
  return snapshot.routes.filter((route) => route.season === snapshot.season)
}

// ------------------------------------------------------- Préparation des commandes

/** Versions attendues : la route cible et chaque route qui porte déjà l'une des adresses. */
export function expectedVersionsFor(
  snapshot: RoutePlanningSnapshot,
  routeId: string,
  locationIds: readonly string[],
): Record<string, number> {
  const versionById = new Map(snapshot.routes.map((route) => [route.id, route.version]))
  const wanted = new Set(locationIds)
  const touched = new Set([routeId])
  for (const location of snapshot.locations) {
    if (wanted.has(location.id) && location.routeId) touched.add(location.routeId)
  }
  const versions: Record<string, number> = {}
  for (const id of touched) {
    const version = versionById.get(id)
    if (version !== undefined) versions[id] = version
  }
  return versions
}

export type RouteAnchor = { beforeLocationId: string } | { afterLocationId: string } | Record<string, never>

/**
 * Ancre pour placer `locationId` à l'index `targetIndex` (0 = en tête) d'une route dont l'ordre courant est
 * `memberIds`. L'adresse déplacée est ignorée dans le calcul : « à la position 3 » reste la 3e place finale.
 */
export function anchorForIndex(
  memberIds: readonly string[],
  locationId: string,
  targetIndex: number,
): RouteAnchor {
  const others = memberIds.filter((id) => id !== locationId)
  if (others.length === 0) return {}
  const index = Math.min(Math.max(Math.trunc(targetIndex), 0), others.length)
  const before = others[index]
  if (before !== undefined) return { beforeLocationId: before }
  const last = others.at(-1)
  return last === undefined ? {} : { afterLocationId: last }
}

export function placeCommand(
  snapshot: RoutePlanningSnapshot,
  input: { idempotencyKey: string; routeId: string; locationIds: readonly string[]; anchor?: RouteAnchor },
): PlaceRouteLocationsCommand {
  return {
    idempotencyKey: input.idempotencyKey,
    routeId: input.routeId,
    locationIds: [...input.locationIds],
    expectedVersions: expectedVersionsFor(snapshot, input.routeId, input.locationIds),
    ...(input.anchor ?? {}),
  }
}

/** Commande « Annuler » d'un retrait : réinsertion près du voisin mémorisé, refusée si la route a changé. */
export function undoRemovalCommand(
  result: RemoveRouteLocationResult,
  idempotencyKey: string,
): PlaceRouteLocationsCommand {
  const { undo } = result
  return {
    idempotencyKey,
    routeId: undo.routeId,
    locationIds: [undo.locationId],
    expectedVersions: { [undo.routeId]: result.routeVersion },
    ...(undo.afterLocationId
      ? { afterLocationId: undo.afterLocationId }
      : undo.beforeLocationId
        ? { beforeLocationId: undo.beforeLocationId }
        : {}),
  }
}

// ---------------------------------------------------------------- Erreurs

export type RouteMutationErrorKind = 'conflict' | 'forbidden' | 'invalid' | 'uncertain'

/** Erreur d'écriture Routes : `code` est le code métier du serveur (ROUTE_VERSION_CONFLICT, …). */
export class RouteMutationError extends Error {
  readonly kind: RouteMutationErrorKind
  readonly code: string

  constructor(kind: RouteMutationErrorKind, code: string) {
    super(code)
    this.name = 'RouteMutationError'
    this.kind = kind
    this.code = code
  }
}

const conflictCodes = new Set([
  'ROUTE_VERSION_CONFLICT',
  'FLAG_VERSION_CONFLICT',
  'POSITION_VERSION_CONFLICT',
  'ROUTE_ANCHOR_CHANGED',
  'ROUTE_LOCATION_NOT_IN_ROUTE',
])

/**
 * Classe une réponse d'erreur PostgREST. Sans code métier reconnu (réseau coupé, délai dépassé, 5xx),
 * le résultat est INCERTAIN : l'écran relit l'état et renvoie, au besoin, la même commande (même clé).
 */
export function classifyRouteMutationError(error: { code?: string; message?: string }): RouteMutationError {
  const message = error.message ?? ''
  const businessCode = /^(ROUTE|POSITION|FLAG)_[A-Z_]+$/.test(message) ? message : null
  if (error.code === '40001' || (businessCode && conflictCodes.has(businessCode))) {
    return new RouteMutationError('conflict', businessCode ?? 'ROUTE_VERSION_CONFLICT')
  }
  if (businessCode?.endsWith('_FORBIDDEN') || error.code === '42501') {
    return new RouteMutationError('forbidden', businessCode ?? 'ROUTE_FORBIDDEN')
  }
  if (businessCode) return new RouteMutationError('invalid', businessCode)
  if (message === 'UNAUTHENTICATED') return new RouteMutationError('forbidden', 'UNAUTHENTICATED')
  return new RouteMutationError('uncertain', 'ROUTE_MUTATION_UNCERTAIN')
}
